import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinSearchAccordionComponent } from '../../shared/design-system/search-accordion.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { LocalizationService } from '../../core/localization/localization.service';
import {
  LEGACY_MODULE_KEY_MAP,
  NAVIGATION_MODULES,
  NAVIGATION_SUITES
} from '../../core/navigation/navigation-taxonomy';
import { IdentityAdminService, PermissionGrantInput, PermissionTreeNode } from './identity-admin.service';

interface PermissionTreeRow {
  key: string;
  displayName: string;
  actions: string[];
  depth: number;
}

interface PermissionModuleGroup {
  rootKey: string;
  title: string;
  rows: PermissionTreeRow[];
  grantedCount: number;
  totalCount: number;
  checked: boolean;
  indeterminate: boolean;
}

interface PermissionSuiteGroup {
  key: string;
  label: string;
  sortOrder: number;
  modules: PermissionModuleGroup[];
}

@Component({
  standalone: true,
  selector: 'ain-identity-role-permissions',
  imports: [
    AinPageComponent,
    AinSearchAccordionComponent,
    AinStateComponent,
    InfoHintComponent,
    FormsModule,
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    SelectModule,
    RouterLink
  ],
  template: `
    <ain-page [eyebrow]="t('identity.eyebrow')" [title]="t('identity.rolePermissions.title')" [description]="t('identity.rolePermissions.description')">
      <div page-actions>
        <ain-info-hint
          titleKey="identity.rolePermissions.hint.title"
          descriptionKey="identity.rolePermissions.hint.description"
          [bulletKeys]="['identity.rolePermissions.hint.bullet1', 'identity.rolePermissions.hint.bullet2', 'identity.rolePermissions.hint.bullet3']" />
        <a routerLink="/identity/roles" class="back-link">{{ t('common.back') }}</a>
        <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load()" [disabled]="loading() || saving()"></button>
        <button pButton type="button" [label]="t('common.save')" (click)="save()" [disabled]="loading() || saving()" [loading]="saving()"></button>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.loadFailed')" [message]="error() ?? ''" />
      } @else {
        <ain-search-accordion [title]="t('platform.search.title')">
          <section class="search-grid">
            <input pInputText [ngModel]="query()" (ngModelChange)="query.set($event)" [placeholder]="t('identity.rolePermissions.search')" />
            <p-select
              [options]="suiteFilterOptions()"
              optionLabel="label"
              optionValue="value"
              [ngModel]="suiteKey()"
              (ngModelChange)="suiteKey.set($event ?? '')"
              appendTo="body"
              [showClear]="false" />
          </section>
        </ain-search-accordion>

        @if (suiteGroups().length === 0) {
          <ain-state type="empty" [title]="t('common.noRecords')" />
        } @else {
          @for (suite of suiteGroups(); track suite.key) {
            <section class="suite-card">
              <header class="suite-header">
                <h2>{{ suite.label }}</h2>
                <span class="suite-count">{{ suite.modules.length }}</span>
              </header>

              @for (module of suite.modules; track module.rootKey) {
                <article class="module-card">
                  <header class="module-header">
                    <div class="module-toggle">
                      <p-checkbox
                        [binary]="true"
                        [ngModel]="module.checked"
                        [indeterminate]="module.indeterminate"
                        (ngModelChange)="toggleModule(module, $event)" />
                      <button pButton type="button" class="module-title" [text]="true" (click)="toggleExpanded(module.rootKey)">
                        {{ module.title }}
                      </button>
                    </div>
                    <span class="module-count">{{ grantedLabel(module.grantedCount, module.totalCount) }}</span>
                    <div class="module-actions">
                      <button pButton type="button" size="small" severity="secondary" [label]="t('identity.rolePermissions.viewAccess')" (click)="grantView(module)"></button>
                      <button pButton type="button" size="small" [label]="t('identity.rolePermissions.fullAccess')" (click)="grantFull(module)"></button>
                      <button pButton type="button" size="small" severity="secondary" [label]="t('identity.rolePermissions.clearSystem')" (click)="clearModule(module)"></button>
                    </div>
                  </header>

                  @if (isExpanded(module.rootKey)) {
                    <div class="tree">
                      <header class="row header">
                        <span>{{ t('columns.name') }}</span>
                        <span>{{ t('identity.rolePermissions.actions') }}</span>
                      </header>
                      @for (row of module.rows; track row.key) {
                        <div class="row">
                          <span class="name" [style.padding-inline-start.px]="row.depth * 20">{{ row.displayName }}</span>
                          <div class="actions">
                            @for (action of row.actions; track action) {
                              <label>
                                <p-checkbox [binary]="true" [ngModel]="isChecked(row.key, action)" (ngModelChange)="toggle(row.key, action, $event)" />
                                <span>{{ actionLabel(action) }}</span>
                              </label>
                            }
                          </div>
                        </div>
                      }
                    </div>
                  }
                </article>
              }
            </section>
          }
        }
      }
    </ain-page>
  `,
  styles: [`
    .search-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 8px; align-items: center; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; align-self: center; }
    .suite-card { display: grid; gap: 10px; margin-bottom: 18px; }
    .suite-header { display: flex; align-items: center; gap: 10px; }
    .suite-header h2 { margin: 0; font-size: 1.05rem; }
    .suite-count, .module-count { color: #475569; font-size: 0.9rem; white-space: normal; }
    .module-card { border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; background: #fff; }
    .module-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      flex-wrap: wrap;
      padding: 10px 12px;
      background: #f8fafc;
    }
    .module-toggle { display: inline-flex; align-items: center; gap: 8px; min-width: min(100%, 18rem); }
    .module-title { font-weight: 700; justify-content: flex-start; white-space: normal; text-align: start; }
    .module-actions { display: flex; flex-wrap: wrap; gap: 8px; }
    .tree { border-top: 1px solid #e2e8f0; }
    .row { display: grid; grid-template-columns: minmax(12rem, 1.1fr) minmax(0, 2fr); gap: 8px; align-items: start; padding: 10px 12px; border-bottom: 1px solid #f1f5f9; }
    .row:last-child { border-bottom: 0; }
    .header { font-weight: 700; background: #fff; }
    .actions { display: flex; flex-wrap: wrap; gap: 10px; }
    label { display: inline-flex; align-items: center; gap: 6px; }
    .name { font-weight: 500; white-space: normal; }
    @media (max-width: 1100px) {
      .search-grid, .row { grid-template-columns: 1fr; }
    }
  `]
})
export class IdentityRolePermissionsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(IdentityAdminService);
  private readonly localizationService = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly permissionTree = signal<PermissionTreeNode[]>([]);
  readonly assigned = signal<Record<string, Set<string>>>({});
  readonly expandedKeys = signal<Set<string>>(new Set());
  readonly query = signal('');
  readonly suiteKey = signal('');

  readonly suiteGroups = computed(() => this.buildSuiteGroups());
  readonly suiteFilterOptions = computed(() => {
    const options = this.buildSuiteGroups(true).map(suite => ({ label: suite.label, value: suite.key }));
    return [{ label: this.t('identity.rolePermissions.allSystems'), value: '' }, ...options];
  });

  private roleId = '';

  ngOnInit(): void {
    const roleId = this.route.snapshot.paramMap.get('roleId');
    if (!roleId) {
      this.error.set(this.t('platform.tenantDetail.errorInvalidId'));
      this.loading.set(false);
      return;
    }

    this.roleId = roleId;
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);

    forkJoin({
      tree: this.api.listPermissionTree(),
      grants: this.api.listRolePermissions(this.roleId)
    }).subscribe({
      next: ({ tree, grants }) => {
        const map: Record<string, Set<string>> = {};
        for (const row of grants) {
          if (!map[row.permissionKey]) map[row.permissionKey] = new Set<string>();
          map[row.permissionKey].add(row.action.toLowerCase());
        }
        this.permissionTree.set(tree);
        this.assigned.set(map);
        this.expandedKeys.set(this.defaultExpandedKeys(tree, map));
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('common.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  isChecked(permissionKey: string, action: string): boolean {
    return this.assigned()[permissionKey]?.has(action.toLowerCase()) ?? false;
  }

  isExpanded(rootKey: string): boolean {
    return this.expandedKeys().has(rootKey) || this.query().trim().length > 0;
  }

  toggleExpanded(rootKey: string): void {
    this.expandedKeys.update(current => {
      const next = new Set(current);
      if (next.has(rootKey)) next.delete(rootKey);
      else next.add(rootKey);
      return next;
    });
  }

  toggle(permissionKey: string, action: string, checked: boolean): void {
    this.assigned.update(current => this.cloneAssigned(current, next => {
      if (!next[permissionKey]) next[permissionKey] = new Set<string>();
      if (checked) next[permissionKey].add(action.toLowerCase());
      else next[permissionKey].delete(action.toLowerCase());
    }));
  }

  toggleModule(module: PermissionModuleGroup, checked: boolean): void {
    if (checked) this.grantFull(module);
    else this.clearModule(module);
  }

  grantView(module: PermissionModuleGroup): void {
    this.assigned.update(current => this.cloneAssigned(current, next => {
      for (const row of module.rows) {
        const hasView = row.actions.some(action => action.toLowerCase() === 'view');
        if (!hasView) continue;
        next[row.key] = new Set(row.actions.filter(action => action.toLowerCase() === 'view').map(action => action.toLowerCase()));
      }
    }));
    this.expandModule(module.rootKey);
  }

  grantFull(module: PermissionModuleGroup): void {
    this.assigned.update(current => this.cloneAssigned(current, next => {
      for (const row of module.rows) {
        next[row.key] = new Set(row.actions.map(action => action.toLowerCase()));
      }
    }));
    this.expandModule(module.rootKey);
  }

  clearModule(module: PermissionModuleGroup): void {
    this.assigned.update(current => this.cloneAssigned(current, next => {
      for (const row of module.rows) {
        next[row.key] = new Set<string>();
      }
    }));
  }

  save(): void {
    const grants: PermissionGrantInput[] = [];
    for (const [permissionKey, actions] of Object.entries(this.assigned())) {
      for (const action of actions) {
        grants.push({
          permissionKey,
          action,
          effect: 0,
          scopeType: 0,
          organizationUnitId: null
        });
      }
    }

    this.saving.set(true);
    this.api.updateRolePermissions(this.roleId, grants).subscribe({
      next: () => {
        this.saving.set(false);
        this.load();
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('common.saveFailed'));
      }
    });
  }

  grantedLabel(granted: number, total: number): string {
    return this.t('identity.rolePermissions.grantedCount')
      .replace('{granted}', `${granted}`)
      .replace('{total}', `${total}`);
  }

  actionLabel(action: string): string {
    const key = `identity.rolePermissions.action.${action.toLowerCase()}`;
    const localized = this.t(key);
    return localized === key ? action : localized;
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }

  private expandModule(rootKey: string): void {
    this.expandedKeys.update(current => {
      const next = new Set(current);
      next.add(rootKey);
      return next;
    });
  }

  private cloneAssigned(
    current: Record<string, Set<string>>,
    mutate: (next: Record<string, Set<string>>) => void
  ): Record<string, Set<string>> {
    const next: Record<string, Set<string>> = {};
    for (const [key, value] of Object.entries(current)) next[key] = new Set(value);
    mutate(next);
    return next;
  }

  private defaultExpandedKeys(tree: PermissionTreeNode[], assigned: Record<string, Set<string>>): Set<string> {
    const roots = tree.filter(node => !node.parentKey);
    const expanded = new Set<string>();
    for (const root of roots) {
      const rows = this.flattenTree(tree, root.key);
      const hasGrant = rows.some(row => (assigned[row.key]?.size ?? 0) > 0);
      if (hasGrant) expanded.add(root.key);
    }
    return expanded;
  }

  private buildSuiteGroups(ignoreFilters = false): PermissionSuiteGroup[] {
    const tree = this.permissionTree();
    const assigned = this.assigned();
    const query = ignoreFilters ? '' : this.query().trim().toLowerCase();
    const suiteFilter = ignoreFilters ? '' : this.suiteKey().trim();
    const suites = new Map<string, PermissionSuiteGroup>();

    for (const root of tree.filter(node => !node.parentKey)) {
      const rows = this.flattenTree(tree, root.key);
      const matchingRows = query
        ? rows.filter(row =>
            row.displayName.toLowerCase().includes(query) ||
            row.key.toLowerCase().includes(query) ||
            row.actions.some(action => this.actionLabel(action).toLowerCase().includes(query)))
        : rows;
      if (matchingRows.length === 0) continue;

      const suite = this.resolveSuite(root.key);
      if (suiteFilter && suite.key !== suiteFilter) continue;

      let grantedCount = 0;
      let totalCount = 0;
      for (const row of matchingRows) {
        totalCount += row.actions.length;
        grantedCount += row.actions.filter(action => assigned[row.key]?.has(action.toLowerCase())).length;
      }

      const module: PermissionModuleGroup = {
        rootKey: root.key,
        title: root.displayName,
        rows: matchingRows,
        grantedCount,
        totalCount,
        checked: totalCount > 0 && grantedCount === totalCount,
        indeterminate: grantedCount > 0 && grantedCount < totalCount
      };

      const existing = suites.get(suite.key);
      if (existing) {
        existing.modules.push(module);
      } else {
        suites.set(suite.key, {
          key: suite.key,
          label: this.t(suite.labelKey),
          sortOrder: suite.sortOrder,
          modules: [module]
        });
      }
    }

    return [...suites.values()]
      .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label))
      .map(suite => ({
        ...suite,
        modules: suite.modules.sort((a, b) => a.title.localeCompare(b.title))
      }));
  }

  private resolveSuite(rootKey: string): { key: string; labelKey: string; sortOrder: number } {
    const mapped = LEGACY_MODULE_KEY_MAP[rootKey] ?? rootKey;
    const navModule = NAVIGATION_MODULES.find(item => item.key === mapped);
    const suite = navModule ? NAVIGATION_SUITES.find(item => item.key === navModule.suiteKey) : undefined;
    return suite ?? { key: 'other', labelKey: 'identity.rolePermissions.otherSystems', sortOrder: 999 };
  }

  private flattenTree(nodes: PermissionTreeNode[], rootKey: string): PermissionTreeRow[] {
    const byParent = new Map<string | null, PermissionTreeNode[]>();
    for (const node of nodes) {
      const parent = node.parentKey ?? null;
      if (!byParent.has(parent)) byParent.set(parent, []);
      byParent.get(parent)!.push(node);
    }

    for (const list of byParent.values()) {
      list.sort((a, b) => a.displayName.localeCompare(b.displayName) || a.key.localeCompare(b.key));
    }

    const root = nodes.find(node => node.key === rootKey);
    if (!root) return [];

    const output: PermissionTreeRow[] = [{
      key: root.key,
      displayName: root.displayName,
      actions: [...root.actions].sort(),
      depth: 0
    }];

    const visit = (parent: string, depth: number): void => {
      const children = byParent.get(parent) ?? [];
      for (const child of children) {
        output.push({
          key: child.key,
          displayName: child.displayName,
          actions: [...child.actions].sort(),
          depth
        });
        visit(child.key, depth + 1);
      }
    };

    visit(root.key, 1);
    return output;
  }
}
